import { assertFmEvidenceDescriptor } from './FmEvidence.validation';
import { assertDashboardRegionRecipe } from './dashboardOverviewRecipes';
import {
  validateDashboardAssistantAction,
  validateDashboardRecordAssistantContext,
} from './FmDashboardOverview.assistantValidation';
import {
  dashboardExact as exact,
  dashboardRecord as record,
  dashboardRegisterId as registerId,
  dashboardString as string,
  dashboardStringArray as stringArray,
  dashboardValidationFail as fail,
  type DashboardUnknownRecord as UnknownRecord,
} from './FmDashboardOverview.validationHelpers';
import {
  array,
  CHIP_VARIANTS,
  RANKED_EVIDENCE_RECORD_KEYS,
  RANKED_RECORD_KEYS,
  RANKED_SECTION_KEYS,
  optionalStrings,
  scalar,
  strings,
  validateAction,
  validateRemoveAction,
} from './FmDashboardOverview.validationShared';
import {
  validateExpertTable,
  validateMedia,
  validateState,
} from './FmDashboardOverview.regionValidation';

function validateTopRecord(
  records: unknown[],
  topRecordId: string | null,
  recordIds: Set<string>,
  path: string,
): void {
  if (records.length === 0) {
    if (topRecordId !== null) {
      fail(`${path}.topRecordId`, 'must be null when no ranked record exists');
    }
    return;
  }
  if (topRecordId !== record(records[0], `${path}.records[0]`).id) {
    fail(`${path}.topRecordId`, 'must reference the rank 1 record');
  }
  if (topRecordId === null || !recordIds.has(topRecordId)) {
    fail(`${path}.topRecordId`, 'must reference a record');
  }
}

function validateRankedMedia(item: UnknownRecord, recordPath: string): void {
  if (item.propertyMap !== undefined) {
    const path = `${recordPath}.propertyMap`;
    const map = record(item.propertyMap, path);
    exact(map, ['propertyId', 'caption'], path);
    string(map.caption, `${path}.caption`);
    if (map.propertyId !== item.propertyId) fail(path, 'must map the record property');
    if (item.mediaContext !== undefined || item.mediaCueLabels !== undefined) {
      fail(path, 'must not combine location maps with illustrative media');
    }
  }
  if (item.mediaContext !== undefined) {
    validateMedia(item.mediaContext, `${recordPath}.mediaContext`);
    if (item.mediaCueLabels === undefined) {
      fail(`${recordPath}.mediaCueLabels`, 'is required when mediaContext is present');
    }
  }
  if (item.mediaCueLabels === undefined) return;
  const cuePath = `${recordPath}.mediaCueLabels`;
  const cue = record(item.mediaCueLabels, cuePath);
  exact(cue, ['context', 'actual', 'illustrative', 'unavailable', 'emptyDescription'], cuePath);
  strings(cue, cuePath, ['context', 'actual', 'illustrative', 'unavailable', 'emptyDescription']);
}

/** An evidence record carries its change summary. */
function isEvidenceRecord(item: UnknownRecord): boolean {
  return Object.prototype.hasOwnProperty.call(item, 'changeSummary');
}

function validateRankedEvidenceFields(
  item: UnknownRecord,
  recordPath: string,
  evidenceRecords: boolean,
): void {
  if (!evidenceRecords) return;
  string(item.changeSummary, `${recordPath}.changeSummary`);
  stringArray(item.changeIds, `${recordPath}.changeIds`);
}

function validateSummaryFields(value: unknown, path: string): void {
  const fields = array(value, path);
  if (fields.length > 4) fail(path, 'must contain at most 4 items');
  const ids = new Set<string>();
  fields.forEach((entry, index) => {
    const fieldPath = `${path}[${index}]`;
    const field = record(entry, fieldPath);
    exact(field, ['id', 'label', 'value', 'unit', 'detail'], fieldPath);
    registerId(field.id, `${fieldPath}.id`, ids);
    string(field.label, `${fieldPath}.label`);
    scalar(field.value, `${fieldPath}.value`);
    optionalStrings(field, fieldPath, ['unit', 'detail']);
  });
}

function validateExpertValues(value: unknown, recordPath: string, columns: UnknownRecord[]): void {
  const expertValues = record(value, `${recordPath}.expertValues`);
  if (Object.keys(expertValues).length > 8) {
    fail(`${recordPath}.expertValues`, 'must contain at most 8 values');
  }
  Object.entries(expertValues).forEach(([key, entry]) => {
    string(key, `${recordPath}.expertValues key`);
    scalar(entry, `${recordPath}.expertValues.${key}`);
  });
  const expertKeys = new Set(
    columns.flatMap((column) => {
      const descriptor = record(column.value, `${recordPath}.expertTable.columns.value`);
      return descriptor.field === 'expert' ? [String(descriptor.key)] : [];
    }),
  );
  Object.keys(expertValues).forEach((key) => {
    if (!expertKeys.has(key)) {
      fail(`${recordPath}.expertValues.${key}`, 'must be referenced by an expert column');
    }
  });
  columns.forEach((column, index) => {
    const descriptor = record(column.value, `${recordPath}.expertTable.columns[${index}].value`);
    if (descriptor.kind === 'expert' && !(String(descriptor.key) in expertValues)) {
      fail(`${recordPath}.expertValues`, `must provide expert value "${String(descriptor.key)}"`);
    }
  });
}

function validateEvidence(
  value: unknown,
  recordPath: string,
): {
  sourceId: string;
  revision: string;
  claimId: string;
  citationIds: string[];
} {
  assertFmEvidenceDescriptor(value);
  const evidence = record(value, `${recordPath}.evidence`);
  const sources = array(evidence.sources, `${recordPath}.evidence.sources`);
  const firstSource = record(sources[0], `${recordPath}.evidence.sources[0]`);
  string(firstSource.revision, `${recordPath}.evidence.sources[0].revision`);
  const citations = array(evidence.citations, `${recordPath}.evidence.citations`);
  const citationIds = citations.map((entry, index) => {
    const citationPath = `${recordPath}.evidence.citations[${index}]`;
    return string(record(entry, citationPath).id, `${citationPath}.id`);
  });
  const claim = record(evidence.claim, `${recordPath}.evidence.claim`);
  const dates = record(evidence.dates, `${recordPath}.evidence.dates`);
  if (!dates.effectiveAt && !dates.observedAt && !dates.retrievedAt) {
    fail(`${recordPath}.evidence.dates`, 'must expose an effective, observed, or retrieved date');
  }
  return {
    sourceId: String(firstSource.id),
    revision: String(firstSource.revision),
    claimId: String(claim.id),
    citationIds,
  };
}

function validateRecordActions(
  item: UnknownRecord,
  recordPath: string,
  ids: Set<string>,
): UnknownRecord {
  const primaryPath = `${recordPath}.primaryAction`;
  const primary = record(item.primaryAction, primaryPath);
  validateAction(primary, primaryPath, ids, { allowIntent: false });
  if (item.detailsAction !== undefined) {
    validateAction(item.detailsAction, `${recordPath}.detailsAction`, ids, { allowIntent: false });
  }
  if (item.secondaryAction !== undefined) {
    const path = `${recordPath}.secondaryAction`;
    const secondary = record(item.secondaryAction, path);
    validateAction(secondary, path, ids, { allowIntent: false, allowIcon: true });
    if (!['overview', 'manage'].includes(String(secondary.icon))) {
      fail(`${path}.icon`, 'must be overview or manage');
    }
  }
  if (item.removeAction !== undefined) {
    validateRemoveAction(item.removeAction, `${recordPath}.removeAction`, ids);
  }
  return primary;
}

interface RankedRecordValidationContext {
  evidenceRecords: boolean;
  path: string;
  columns: UnknownRecord[];
  ids: Set<string>;
  handlers: UnknownRecord;
  rankingRunId: string | null;
}

function validateRankedRecord(
  entry: unknown,
  index: number,
  context: RankedRecordValidationContext,
): { id: string; entityId: string } {
  const { path, columns, ids, handlers, rankingRunId, evidenceRecords } = context;
  const recordPath = `${path}.records[${index}]`;
  const item = record(entry, recordPath);
  if (isEvidenceRecord(item) !== evidenceRecords) {
    fail(recordPath, 'must match the collection: all entity records or all evidence records');
  }
  exact(item, evidenceRecords ? RANKED_EVIDENCE_RECORD_KEYS : RANKED_RECORD_KEYS, recordPath);
  const id = registerId(item.id, `${recordPath}.id`, ids);
  const entityId = string(item.entityId, `${recordPath}.entityId`);
  string(item.propertyId, `${recordPath}.propertyId`);
  if (item.rank !== index + 1) {
    fail(`${recordPath}.rank`, `must be contiguous rank ${index + 1}`);
  }
  strings(item, recordPath, ['title', 'stateSentence', 'reason']);
  validateRankedEvidenceFields(item, recordPath, evidenceRecords);
  const status = record(item.status, `${recordPath}.status`);
  exact(status, ['label', 'variant'], `${recordPath}.status`);
  string(status.label, `${recordPath}.status.label`);
  if (status.variant !== undefined && !CHIP_VARIANTS.has(String(status.variant))) {
    fail(`${recordPath}.status.variant`, 'contains an unsupported variant');
  }
  validateRankedMedia(item, recordPath);
  validateSummaryFields(item.summaryFields, `${recordPath}.summaryFields`);
  validateExpertValues(item.expertValues, recordPath, columns);
  const evidence = validateEvidence(item.evidence, recordPath);
  const primaryAction = validateRecordActions(item, recordPath, ids);
  const assistantAction = validateDashboardAssistantAction(
    item.assistantAction,
    `${recordPath}.assistantAction`,
    ids,
    handlers,
  );
  validateDashboardRecordAssistantContext(assistantAction, `${recordPath}.assistantAction`, {
    rankingRunId:
      rankingRunId === null
        ? fail(`${path}.rankingRunId`, 'must identify served records')
        : rankingRunId,
    recordId: id,
    entityId,
    propertyId: String(item.propertyId),
    jobId: String(primaryAction.jobId),
    sourceId: evidence.sourceId,
    revision: evidence.revision,
    claimId: evidence.claimId,
    changeSummary: evidenceRecords ? String(item.changeSummary) : undefined,
    changeIds: evidenceRecords ? (item.changeIds as string[]) : undefined,
    citationIds: evidence.citationIds,
  });
  return { id, entityId };
}

function validateRankedCollectionState(
  state: string,
  records: unknown[],
  rankingRunId: string | null,
  topRecordId: string | null,
  path: string,
): void {
  if (['ready', 'partial', 'stale'].includes(state) && records.length === 0) {
    fail(`${path}.records`, `must contain records when state is ${state}`);
  }
  if (['loading', 'empty', 'error'].includes(state) && records.length > 0) {
    fail(`${path}.records`, `must be empty when state is ${state}`);
  }
  if (records.length > 0 && rankingRunId === null) {
    fail(`${path}.rankingRunId`, 'must identify the served ranking when records are present');
  }
  if (records.length === 0 && topRecordId !== null) {
    fail(`${path}.topRecordId`, 'must be null when no ranked record exists');
  }
}

function validateRankedRecords(
  records: unknown[],
  context: RankedRecordValidationContext,
): Set<string> {
  const recordIds = new Set<string>();
  const entityIds = new Set<string>();
  records.forEach((entry, index) => {
    const current = validateRankedRecord(entry, index, context);
    recordIds.add(current.id);
    if (entityIds.has(current.entityId)) {
      fail(`${context.path}.records[${index}].entityId`, `duplicates entity "${current.entityId}"`);
    }
    entityIds.add(current.entityId);
  });
  return recordIds;
}

export function validateRankedCollection(
  value: unknown,
  path: string,
  ids: Set<string>,
  handlers: UnknownRecord,
): UnknownRecord {
  const section = record(value, path);
  exact(section, RANKED_SECTION_KEYS, path);
  if (section.kind !== 'collection') {
    fail(`${path}.kind`, `must be collection, not "${String(section.kind)}"`);
  }
  registerId(section.id, `${path}.id`, ids);
  if (section.showRank !== undefined && typeof section.showRank !== 'boolean') {
    fail(`${path}.showRank`, 'must be a boolean');
  }
  strings(section, path, ['ownerQuestion', 'literalAnswer', 'description', 'ariaLabel']);
  const state = validateState(section.state, `${path}.state`, ids);
  const rankingRunId =
    section.rankingRunId === null ? null : string(section.rankingRunId, `${path}.rankingRunId`);
  const topRecordId =
    section.topRecordId === null ? null : string(section.topRecordId, `${path}.topRecordId`);
  const columns = validateExpertTable(section.expertTable, `${path}.expertTable`, ids);
  assertDashboardRegionRecipe(section.recipe, `${path}.recipe`, {
    dominantBeat: 'ranked-entity-grid',
    primaryAction: 'one-record-primary-action',
    expertReveal: 'same-record-table',
    conversionRole: 'triage',
  });
  if (section.evidence !== undefined) assertFmEvidenceDescriptor(section.evidence);
  const records = Array.isArray(section.records)
    ? section.records
    : fail(`${path}.records`, 'must be an array');
  // The collection names its record shape, so an empty collection is checked
  // against the same rules as a full one.
  if (section.recordKind !== 'entity' && section.recordKind !== 'evidence') {
    fail(`${path}.recordKind`, 'must be entity or evidence');
  }
  const evidenceRecords = section.recordKind === 'evidence';
  if (!evidenceRecords && columns.some((column) => record(column.value, '').field === 'change')) {
    fail(`${path}.expertTable.columns`, 'may use change only for evidence records');
  }
  validateRankedCollectionState(state, records, rankingRunId, topRecordId, path);
  const recordIds = validateRankedRecords(records, {
    evidenceRecords,
    path,
    columns,
    ids,
    handlers,
    rankingRunId,
  });
  validateTopRecord(records, topRecordId, recordIds, path);
  return section;
}
